import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, ClipboardList, Trophy, Bell, BookOpen, CalendarCheck,
  Sparkles, ChevronRight, Zap, X, AlertCircle, LayoutGrid,
  HelpCircle, Medal, Award, TrendingUp, Users, Star, Loader2,
  Flame, Target, BarChart2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

// ── Competition Results Modal ─────────────────────────────────────────────────
function CompetitionResultsModal({ comp, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/quiz/competitions/${comp.id}/results`)
      .then(r => { if (r?.success) setData(r.data); })
      .finally(() => setLoading(false));
  }, [comp.id]);

  const MEDALS = ['🥇', '🥈', '🥉'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="competition-results-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-5 text-white relative">
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-5 h-5" />
            <span className="text-xs font-bold opacity-80">Competition Results</span>
          </div>
          <h2 className="font-black text-lg leading-tight">{comp.title}</h2>
          <p className="text-xs opacity-75 mt-0.5">{comp.subject} · {comp.is_free ? 'Free Entry' : `₦${(comp.entry_fee || 0).toLocaleString()}`}</p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : !data ? (
            <p className="text-center text-slate-400 py-10 text-sm">Results not available yet</p>
          ) : (
            <>
              {/* My rank highlight */}
              {data.my_rank && (
                <div className={`rounded-2xl p-4 border ${
                  data.my_rank.rank <= 3 ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
                }`} data-testid="my-competition-rank">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-xl font-black ${
                      data.my_rank.rank <= 3 ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      {data.my_rank.rank <= 3 ? MEDALS[data.my_rank.rank - 1] : `#${data.my_rank.rank}`}
                    </div>
                    <div>
                      <p className="font-black text-slate-800 text-sm">Your Rank</p>
                      <p className="text-xs text-slate-500">
                        {data.my_rank.rank <= 3 ? 'Congratulations!' : `Keep practising!`} · {data.my_rank.percentage}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Trophies */}
              {(data.trophies || []).length > 0 && (
                <div className="bg-amber-50 rounded-2xl p-3 border border-amber-100">
                  <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5" /> Trophies Awarded
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {data.trophies.map((t, i) => (
                      <div key={i} className="flex items-center gap-1.5 bg-white rounded-xl px-2.5 py-1.5 border border-amber-200 shadow-sm">
                        <span className="text-base">{t.trophy_emoji || '🏆'}</span>
                        <div>
                          <p className="text-[10px] font-bold text-amber-700">{t.student_name || 'Winner'}</p>
                          {t.prize_amount && <p className="text-[10px] text-amber-600">₦{t.prize_amount.toLocaleString()}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leaderboard */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-black text-slate-700">
                    Leaderboard · {data.rankings.length} participant{data.rankings.length !== 1 ? 's' : ''}
                  </h3>
                </div>
                <div className="space-y-2" data-testid="competition-leaderboard">
                  {data.rankings.slice(0, 10).map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
                        r.is_me ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-100'
                      }`}
                    >
                      <span className="w-7 text-center text-sm font-black text-slate-400">
                        {r.rank <= 3 ? MEDALS[r.rank - 1] : `#${r.rank}`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${r.is_me ? 'text-[#2563eb]' : 'text-slate-700'}`}>
                          {r.student_name} {r.is_me && <span className="text-[10px] font-normal">(You)</span>}
                        </p>
                        {r.school_name && <p className="text-[10px] text-slate-400 truncate">{r.school_name}</p>}
                      </div>
                      <div className={`text-sm font-black ${r.percentage >= 70 ? 'text-emerald-600' : r.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {r.percentage}%
                      </div>
                    </div>
                  ))}
                  {data.rankings.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No results submitted yet</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


const SUBJECT_PALETTE = [
  { name: 'Mathematics',  emoji: '📐', from: 'from-blue-500',    to: 'to-blue-400',    bg: 'bg-blue-50',    text: 'text-blue-700' },
  { name: 'English',      emoji: '📖', from: 'from-violet-500',  to: 'to-violet-400',  bg: 'bg-violet-50',  text: 'text-violet-700' },
  { name: 'Physics',      emoji: '⚗️', from: 'from-orange-500',  to: 'to-amber-400',   bg: 'bg-orange-50',  text: 'text-orange-700' },
  { name: 'Biology',      emoji: '🌿', from: 'from-emerald-500', to: 'to-green-400',   bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { name: 'Chemistry',    emoji: '🔬', from: 'from-pink-500',    to: 'to-rose-400',    bg: 'bg-pink-50',    text: 'text-pink-700' },
  { name: 'History',      emoji: '📜', from: 'from-amber-500',   to: 'to-yellow-400',  bg: 'bg-amber-50',   text: 'text-amber-700' },
];

const QUICK = [
  { label: 'My Results',     icon: Trophy,        from: 'from-[#2563eb]', to: 'to-[#60a5fa]', bg: 'bg-blue-50', text: 'text-[#2563eb]', path: '/student/results' },
  { label: 'Attendance',     icon: CalendarCheck, from: 'from-emerald-500', to: 'to-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700', path: '/student/attendance' },
  { label: 'Assignments',    icon: ClipboardList, from: 'from-amber-500', to: 'to-amber-400',   bg: 'bg-amber-50',   text: 'text-amber-700',   path: '/student/assignments' },
  { label: 'Announcements',  icon: Bell,          from: 'from-rose-500',  to: 'to-pink-400',    bg: 'bg-rose-50',    text: 'text-rose-600',    path: '/student/announcements' },
];

const AI_TIPS = [
  'Create a study timetable for my upcoming exams',
  'Explain quadratic equations with step-by-step examples',
  'Give me 10 practice questions on photosynthesis',
  'Help me understand the causes of World War I',
];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr).setHours(0,0,0,0) - new Date().setHours(0,0,0,0);
  return Math.ceil(diff / 86400000);
}

export default function StudentDashboard() {
  const { user, school } = useAuth();
  const navigate = useNavigate();
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem('asgn_banner_dismissed') === new Date().toDateString()
  );
  const [dueThisWeek, setDueThisWeek] = useState([]);
  const [soonestDue, setSoonestDue]   = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [quizStats, setQuizStats] = useState(null);
  const [resultsComp, setResultsComp] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [lastPrepSession, setLastPrepSession] = useState(null);
  const [prepStreak, setPrepStreak] = useState(0);

  const firstName = user?.first_name || 'Student';
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const emoji     = hour < 12 ? '🌅' : hour < 17 ? '✌️' : '🌙';
  const today     = new Date().toLocaleDateString('en-NG', { weekday: 'long', day: 'numeric', month: 'long' });

  useEffect(() => {
    api.get('/assignments/student').then(r => {
      if (!r?.success) return;
      const pending = (r.data || []).filter(a => !a.submitted && a.due_date);
      const upcoming = pending.filter(a => {
        const d = daysUntil(a.due_date);
        return d !== null && d >= 0 && d <= 7;
      });
      upcoming.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      setDueThisWeek(upcoming);
      if (upcoming.length > 0) setSoonestDue(upcoming[0]);
    }).catch(() => {});
    // Load competitions and quiz stats
    api.get('/quiz/competitions').then(r => { if (r?.success) setCompetitions((r.data || []).slice(0, 3)); }).catch(() => {});
    api.get('/quiz/my-stats').then(r => { if (r?.success) setQuizStats(r.data); }).catch(() => {});
    api.get('/students/me').then(r => { if (r?.success) setStudentProfile(r.data); }).catch(() => {});
    api.get('/exam-prep/sessions?limit=1').then(r => { if (r?.success && r.data?.length > 0) setLastPrepSession(r.data[0]); }).catch(() => {});
    api.get('/exam-prep/streak').then(r => { if (r?.success) setPrepStreak(r.data?.streak || 0); }).catch(() => {});
  }, []);

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('asgn_banner_dismissed', new Date().toDateString());
  };

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-5" data-testid="student-dashboard-page">

      {/* ── Header Row ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 leading-tight">
            {greeting}, {firstName}! {emoji}
          </h1>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <p className="text-slate-500 text-sm">{today} &bull; {school?.name || 'School'}</p>
            {(studentProfile?.class_name || studentProfile?.section) && (
              <span className="inline-flex items-center gap-1 bg-[#2563eb]/10 text-[#2563eb] text-xs font-bold px-2.5 py-0.5 rounded-full" data-testid="student-class-badge">
                {studentProfile.class_name}
                {studentProfile.section && ` · Arm ${studentProfile.section}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 bg-amber-100 border border-amber-200 text-amber-700 px-3 py-1.5 rounded-2xl text-sm font-bold flex-shrink-0"
          data-testid="streak-badge">
          <Flame className="w-4 h-4 text-orange-500" />
          <span>{prepStreak > 0 ? `${prepStreak} day streak` : 'Start a streak!'}</span>
        </div>
      </div>

      {/* ── Assignment Reminder Banner ── */}
      {dueThisWeek.length > 0 && !bannerDismissed && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-center gap-3"
          data-testid="assignment-reminder-banner"
        >
          <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-800">
              {dueThisWeek.length} assignment{dueThisWeek.length > 1 ? 's' : ''} due this week
            </p>
            {soonestDue && (
              <p className="text-xs text-amber-600 mt-0.5">
                Closest: <span className="font-semibold">{soonestDue.title}</span>
                {' '}—{' '}
                {daysUntil(soonestDue.due_date) === 0
                  ? 'due today!'
                  : daysUntil(soonestDue.due_date) === 1
                  ? 'due tomorrow'
                  : `${daysUntil(soonestDue.due_date)} days left`}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/student/assignments')}
            className="text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-xl transition-colors flex-shrink-0"
            data-testid="reminder-view-assignments"
          >
            View
          </button>
          <button onClick={dismissBanner} className="text-amber-400 hover:text-amber-600 transition-colors flex-shrink-0" data-testid="dismiss-reminder">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Hero Banner ── */}
      <div
        className="relative overflow-hidden rounded-3xl p-6 shadow-lg shadow-[#2563eb]/20"
        style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d58d4 45%, #1e40af 100%)' }}
        data-testid="student-hero-banner"
      >
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden>
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/5" />
          <div className="absolute bottom-0 left-1/3 w-44 h-44 rounded-full bg-white/5" />
          <div className="absolute top-1/2 right-1/4 w-24 h-24 rounded-full bg-white/8" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/20 text-white px-3 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> Student Dashboard
            </span>
          </div>
          <h2 className="text-white font-black text-2xl mb-1">Keep Up the Great Work!</h2>
          <p className="text-white/80 text-sm mb-5 max-w-md">
            Track your academic progress, assignments, and attendance. Use the AI Tutor to get help with any subject.
          </p>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/student/results')}
              className="bg-white text-[#2563eb] font-bold px-5 py-2.5 rounded-full text-sm hover:bg-white/90 active:scale-95 transition-transform duration-150 shadow-sm"
              data-testid="hero-view-results-btn"
            >
              View My Results
            </button>
            <button
              onClick={() => navigate('/student/ai')}
              className="bg-white/15 border border-white/20 text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-white/25 backdrop-blur-sm transition-colors flex items-center gap-2"
              data-testid="hero-ai-tutor-btn"
            >
              <Brain className="w-4 h-4" /> AI Tutor
            </button>
          </div>
        </div>
      </div>

      {/* ── Quick Access ── */}
      <div>
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Quick Access</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK.map((q) => (
            <button
              key={q.label}
              onClick={() => navigate(q.path)}
              className={`${q.bg} p-4 rounded-2xl text-left hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] transition-all duration-200`}
              data-testid={`quick-link-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${q.from} ${q.to} flex items-center justify-center mb-3 shadow-sm`}>
                <q.icon className="w-5 h-5 text-white" />
              </div>
              <p className={`text-sm font-bold ${q.text}`}>{q.label}</p>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Subjects + AI Tips ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Subjects */}
        <div className="lg:col-span-3 bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[#2563eb]" />
              <h3 className="font-bold text-slate-800 text-sm">My Subjects</h3>
            </div>
            <button
              onClick={() => navigate('/student/results')}
              className="text-xs text-[#2563eb] font-semibold hover:underline"
            >
              View Results →
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {SUBJECT_PALETTE.map((s) => (
              <div
                key={s.name}
                className={`${s.bg} rounded-2xl p-3 flex items-center gap-2.5 hover:shadow-sm transition-shadow cursor-default`}
              >
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s.from} ${s.to} flex items-center justify-center text-sm flex-shrink-0 shadow-sm`}>
                  {s.emoji}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-bold ${s.text} truncate`}>{s.name}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-[10px] text-slate-500">Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Tips */}
        <div
          className="lg:col-span-2 rounded-3xl p-5 shadow-sm flex flex-col"
          style={{ background: 'linear-gradient(160deg, #1a1f3e 0%, #0f1623 100%)' }}
          data-testid="ai-study-help-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#2563eb]/30 flex items-center justify-center">
                <Zap className="w-4 h-4 text-blue-300" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Study Help</p>
                <p className="text-slate-500 text-xs">Ask anything</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/student/ai')}
              className="text-xs text-blue-300 hover:text-blue-200 font-semibold transition-colors"
              data-testid="open-ai-tutor-btn"
            >
              Open →
            </button>
          </div>
          <div className="space-y-2 flex-1">
            {AI_TIPS.map((tip, i) => (
              <button
                key={i}
                onClick={() => navigate('/student/ai')}
                className="w-full text-left text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl px-3.5 py-2.5 transition-colors duration-150 line-clamp-2 leading-relaxed"
                data-testid={`ai-tip-${i}`}
              >
                "{tip}"
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Competitions & Trophies ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Competitions — Upcoming/Live + Completed */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              <h3 className="font-bold text-slate-800 text-sm">Competitions</h3>
            </div>
            <button onClick={() => navigate('/student/quiz')} className="text-xs text-[#2563eb] font-semibold hover:underline">
              View All →
            </button>
          </div>
          {competitions.length === 0 ? (
            <div className="text-center py-6">
              <Trophy className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No competitions yet</p>
              <button onClick={() => navigate('/student/quiz')} className="mt-2 text-xs text-[#2563eb] font-semibold hover:underline">
                Practice in Quiz Zone
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {competitions.map(c => {
                const isDone = c.status === 'completed';
                return (
                  <div
                    key={c.id}
                    className={`flex items-center gap-3 p-3 rounded-2xl ${
                      c.status === 'live' ? 'bg-green-50 border border-green-200' :
                      isDone ? 'bg-slate-50 border border-slate-200' : 'bg-amber-50 border border-amber-100'
                    }`}
                    data-testid={`comp-card-${c.id}`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      c.status === 'live' ? 'bg-green-100' : isDone ? 'bg-slate-200' : 'bg-amber-100'
                    }`}>
                      <Trophy className={`w-4 h-4 ${c.status === 'live' ? 'text-green-600' : isDone ? 'text-slate-500' : 'text-amber-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-700 truncate">{c.title}</p>
                      <p className="text-[10px] text-slate-400">{c.subject} · {c.is_free ? 'Free' : `₦${(c.entry_fee || 0).toLocaleString()}`}</p>
                    </div>
                    {c.status === 'live' && (
                      <span className="text-[10px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full animate-pulse flex-shrink-0">LIVE</span>
                    )}
                    {isDone && (
                      <button
                        onClick={() => setResultsComp(c)}
                        className="text-[10px] font-bold text-[#2563eb] bg-blue-50 border border-blue-200 px-2 py-1 rounded-full hover:bg-blue-100 flex-shrink-0"
                        data-testid={`view-results-${c.id}`}
                      >
                        Results
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Games + Quiz Quick Access */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <LayoutGrid className="w-4 h-4 text-purple-500" />
            <h3 className="font-bold text-slate-800 text-sm">Games & Quiz</h3>
          </div>
          <div className="space-y-2.5">
            <button
              onClick={() => navigate('/student/games')}
              className="w-full flex items-center gap-3 p-3 bg-purple-50 hover:bg-purple-100 rounded-2xl transition-colors"
              data-testid="dashboard-games-btn"
            >
              <div className="w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center">
                <LayoutGrid className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-xs font-bold text-purple-700">Learning Games</p>
                <p className="text-[10px] text-slate-400">Play & earn points</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            </button>
            <button
              onClick={() => navigate('/student/quiz')}
              className="w-full flex items-center gap-3 p-3 bg-pink-50 hover:bg-pink-100 rounded-2xl transition-colors"
              data-testid="dashboard-quiz-btn"
            >
              <div className="w-8 h-8 rounded-xl bg-pink-100 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-pink-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-xs font-bold text-pink-700">Quiz Zone</p>
                <p className="text-[10px] text-slate-400">
                  {quizStats ? `${quizStats.total_attempts} attempts · Best: ${quizStats.best_score}%` : 'Test your knowledge'}
                </p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            </button>

            {/* Exam Prep Card */}
            <button
              onClick={() => navigate('/student/exam-prep')}
              className="w-full flex items-center gap-3 p-3 bg-orange-50 hover:bg-orange-100 rounded-2xl transition-colors border border-orange-100"
              data-testid="dashboard-exam-prep-btn"
            >
              <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-orange-500" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-xs font-bold text-orange-700">AI Exam Prep</p>
                <p className="text-[10px] text-slate-400">
                  {lastPrepSession
                    ? `Last: ${lastPrepSession.subject} · ${lastPrepSession.score_pct ?? '—'}%`
                    : 'Start exam practice'}
                </p>
              </div>
              {lastPrepSession?.weak_topics?.length > 0 && (
                <span className="text-[10px] font-bold text-orange-600 bg-orange-100 border border-orange-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {lastPrepSession.weak_topics.length} weak
                </span>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            </button>

            {/* My Growth Card */}
            <button
              onClick={() => navigate('/student/academic-growth')}
              className="w-full flex items-center gap-3 p-3 bg-cyan-50 hover:bg-cyan-100 rounded-2xl transition-colors"
              data-testid="dashboard-my-growth-btn"
            >
              <div className="w-8 h-8 rounded-xl bg-cyan-100 flex items-center justify-center">
                <BarChart2 className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-xs font-bold text-cyan-700">My Growth</p>
                <p className="text-[10px] text-slate-400">Track your academic progress</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {quizStats && (quizStats.trophies || []).length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-2xl">
                <Medal className="w-4 h-4 text-amber-500" />
                <p className="text-xs font-bold text-amber-700">{quizStats.trophies.length} trophy{quizStats.trophies.length > 1 ? 'ies' : ''} earned!</p>
                <span className="text-sm">{quizStats.trophies[0]?.trophy_emoji}</span>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Competition Results Modal */}
      {resultsComp && <CompetitionResultsModal comp={resultsComp} onClose={() => setResultsComp(null)} />}

    </div>
  );
}
